/**
 * Fonction utilitaire pour déterminer les comptes disponibles lors de la création/édition d'une transaction.
 * 
 * Règles métier :
 * - Si un utilisateur est sélectionné, on offre le choix parmi ses comptes personnels
 * - Sinon, on prend les comptes liés au groupe
 * - On n'affiche que les comptes enabled, sauf si c'est le compte actuellement renseigné (pour permettre l'édition)
 * 
 * Cette séparation permet de distinguer les comptes personnels d'un utilisateur
 * des comptes partagés du foyer (groupe).
 * Le filtrage par enabled évite de proposer des comptes désactivés, tout en permettant
 * de conserver un compte désactivé lors de l'édition d'une transaction existante.
 */

export interface Account {
  id: string;
  name: string;
  enabled?: boolean | null;
}

export interface Group {
  id: string;
  accounts: Array<Account>;
  users: Array<{
    id: string;
    accounts: Array<Account>;
  }>;
}

export interface TransactionValuesData {
  groups?: Group[];
}

/**
 * Formate le label d'un compte en ajoutant un indicateur visuel si le compte est désactivé.
 * Utilisé dans les sélecteurs où les <option> ne supportent pas le CSS avancé.
 */
export function formatAccountLabel(name: string, enabled?: boolean | null): string {
  return enabled === false ? `⚠ ${name} (désactivé)` : name;
}

/**
 * Retourne les comptes disponibles pour une transaction selon la règle métier :
 * - Si userId est fourni, retourne les comptes de cet utilisateur
 * - Sinon, retourne les comptes du groupe
 * - Filtre les comptes non enabled, sauf si c'est le compte actuellement renseigné (currentAccountId)
 *
 * @param transactionValuesData - Les données de sélection des transactions
 * @param groupId - L'ID du groupe sélectionné
 * @param userId - L'ID de l'utilisateur sélectionné (optionnel)
 * @param currentAccountId - L'ID du compte actuellement renseigné (optionnel, pour l'édition)
 * @returns Liste des comptes disponibles avec leur id, name et enabled
 */
export function getAvailableAccounts(
  transactionValuesData: TransactionValuesData | null | undefined,
  groupId: string,
  userId: string | null | undefined,
  currentAccountId?: string | null
): Array<Account> {
  if (!transactionValuesData?.groups) {
    return [];
  }

  const group = transactionValuesData.groups.find((g) => g.id === groupId);
  if (!group) {
    return [];
  }

  let accounts: Array<Account> = [];

  // Si un utilisateur est sélectionné, retourner ses comptes personnels
  // Si l'utilisateur n'a pas de comptes personnels, on revient aux comptes du groupe
  // (un utilisateur peut être associé à une opération sur un compte collectif)
  if (userId) {
    const user = group.users.find((u) => u.id === userId);
    const userAccounts = user?.accounts || [];
    accounts = userAccounts.length > 0 ? userAccounts : (group.accounts || []);
  } else {
    // Sinon, retourner les comptes du groupe
    accounts = group.accounts || [];
  }

  // Filtrer les comptes non enabled, sauf si c'est le compte actuellement renseigné
  // Cela permet de ne pas proposer des comptes désactivés lors de l'ajout,
  // mais de conserver le compte actuel lors de l'édition même s'il est désactivé
  // enabled peut être undefined, null ou false pour un compte désactivé
  return accounts.filter((account) => (account.enabled ?? false) || account.id === currentAccountId);
}

